import React, { useState, useEffect, useRef, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { checkUserIsSeller, checkValidString, getValBySearch } from '../../../utils/helpers';
import { searchProductAsync, setResultSearch, setSearchTerm } from '../../../services/store/storeSlice';
import { memo } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment } from '@mui/material';
import { allLinks } from '../../../utils/helpers';
import { useNavigate } from 'react-router-dom';

function ProductSearch({ resultSearch, search_term, language, auth, dynamicState }) {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {userProfile} = useSelector(state => state.profile);
  const [selectedValue, setSelectedValue] = useState(null);
  const [inputValue, setInputValue] = useState(search_term || '');
  const isFirstRender = useRef(true);
  const { t, i18n } = useTranslation();

  const debouncedSearch = debounce(async (inputValue) => {
    const source = axios.CancelToken.source();
    if (checkValidString(inputValue) && inputValue.length >= 3) {
      dispatch(searchProductAsync({ auth, dynamicState, inputValue, source }));
    } else if (checkUserIsSeller(userProfile) && inputValue.length === 0) {
      dispatch(setSearchTerm(inputValue));
    }

    return () => source.cancel();
  }, 300); 

  const handleSetProductName = (event, value) => {
    dispatch(setSearchTerm(value));
    setSelectedValue(null);
    setInputValue(value);
    
    if (checkUserIsSeller(userProfile)) {
      // For sellers: stay on current page and search for products
      if (checkValidString(value) && value.length >= 3) {
        const source = axios.CancelToken.source();
        dispatch(searchProductAsync({ auth, dynamicState, inputValue: value, source }));
      } else {
        dispatch(setResultSearch([]));
      }
    } else {
      // For non-sellers: navigate to search page and clear results
      dispatch(setResultSearch([]));
      navigate(`/${allLinks[19]}/${value}`); // Redirect to store page with search term
    }
  };

  const handleChange = (event) => {
    const search_term = event.target.value;
    if (search_term === '') dispatch(setResultSearch([]));
    setInputValue(search_term);
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // Skip first render
      return;
    }

    debouncedSearch(inputValue); 

    return () => debouncedSearch.cancel();
  }, [inputValue]); 

  const mappedOptions = useMemo(
    () => resultSearch.map((option) => getValBySearch(option.name, inputValue, language)),
    [resultSearch, inputValue, language]
  );

  return (    
    <Stack>
      <Autocomplete
        freeSolo
        id="searchValue"
        disableClearable
        options={mappedOptions}
        value={selectedValue}
        inputValue={inputValue}
        onChange={handleSetProductName}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('store.product_search')}
            onChange={handleChange}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              type: 'search',
            }}
          />
        )}
      />
    </Stack>
    
  );
}

export default memo(ProductSearch);
